import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaRegEye, FaStar, FaStarHalfAlt, FaRegStar } from 'react-icons/fa';
import { RiHeartFill, RiHeartLine } from 'react-icons/ri';
import { useAppLanguage } from '../../../Hooks/useAppLanguage';
import { useCart } from '../../../Context/CartContext';
import { usePublicSettings } from '../../../Context/PublicSettingsContext';
import { getProductImageUrl } from '../../../Utils/productImage';
import {
    getDiscountPercent,
    getEffectivePrice,
    hasProductDiscount,
} from '../../../Utils/productPricing';
import ProductCardImage from './ProductCardImage';
import './ProductCard.css';

const truncateName = (name, maxLength = 20) => {
    if (!name) return '';
    return name.length > maxLength ? `${name.substring(0, maxLength)}...` : name;
};

const renderProductRating = (rating, className = '') => {
    const value = Number(rating);
    if (!Number.isFinite(value) || value <= 0) {
        return null;
    }

    const clamped = Math.min(5, Math.max(0, value));
    const fullStars = Math.floor(clamped);
    const hasHalf = clamped - fullStars >= 0.5;

    return (
        <div
            className={`product-rating ${className}`.trim()}
            aria-label={`${clamped.toFixed(1)} out of 5`}
        >
            {Array.from({ length: 5 }, (_, index) => {
                if (index < fullStars) {
                    return <FaStar key={index} className="product-rating__star product-rating__star--full" />;
                }
                if (index === fullStars && hasHalf) {
                    return <FaStarHalfAlt key={index} className="product-rating__star product-rating__star--full" />;
                }
                return <FaRegStar key={index} className="product-rating__star" />;
            })}
            <span className="product-rating__value">{clamped.toFixed(1)}</span>
        </div>
    );
};

const renderProductPrice = (product, formatPrice) => {
    const original = Number(product?.price) || 0;
    const effective = getEffectivePrice(product);
    const discount = getDiscountPercent(product);
    const onSale = hasProductDiscount(product);

    if (!onSale) {
        return (
            <span className="product-price">
                {formatPrice(effective)}
            </span>
        );
    }

    return (
        <div className="product-price-group">
            <span className="product-price product-price--sale">
                {formatPrice(effective)}
            </span>
            <span className="product-price product-price--original">
                {formatPrice(original)}
            </span>
            {discount > 0 && (
                <span className="product-price-badge">-{discount}%</span>
            )}
        </div>
    );
};

function ProductCard({ product, className = '', nameMaxLength = 20 }) {
    const { selectedLanguage, dir, langPath, t } = useAppLanguage();
    const navigate = useNavigate();
    const { addToWishlist, removeFromWishlist, isInWishlist } = useCart();
    const { formatPrice } = usePublicSettings();

    const productName = product.name?.[selectedLanguage] || product.name?.en || '';
    const displayName = truncateName(productName, nameMaxLength);
    const detailPath = langPath(`/product-details/${product._id}`);
    const wishlistId = product._id;
    const brand = product.brand?.trim();

    const handleToggleWishlist = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (isInWishlist(wishlistId)) {
            removeFromWishlist(wishlistId);
            return;
        }
        addToWishlist({
            ...product,
            imageUrl: getProductImageUrl(product),
        });
    };

    return (
        <article className={`product-card ${className}`.trim()}>
            <div>
                <div className="product-img-container">
                    {product.isNewItem && (
                        <span className="product-card-badge product-card-badge--new">
                            {t('productCard.new')}
                        </span>
                    )}
                    <Link to={detailPath} className="product-img-link">
                        <ProductCardImage
                            product={product}
                            productName={productName}
                            alt={productName}
                        />
                    </Link>
                    <button
                        type="button"
                        className={`product-wishlist-button ${isInWishlist(wishlistId) ? 'active' : ''}`}
                        onClick={handleToggleWishlist}
                        title={t('wishlist.addToWishlist')}
                        aria-label={isInWishlist(wishlistId) ? t('wishlist.inWishlist') : t('wishlist.addToWishlist')}
                    >
                        {isInWishlist(wishlistId) ? <RiHeartFill /> : <RiHeartLine />}
                    </button>
                </div>

                <div className="product-card-details" dir={dir}>
                    <p className="product-name">{displayName}</p>
                    {brand && (
                        <p className="product-brand">{brand}</p>
                    )}
                    <div className="product-card-rating-row">
                        {renderProductRating(product.rating, 'product-card-rating')}
                    </div>
                    <div className="product-card-price-row">
                        {renderProductPrice(product, formatPrice)}
                    </div>
                </div>
            </div>
            <div className="product-button-container">
                <button
                    type="button"
                    className="product-card-button"
                    onClick={() => navigate(detailPath)}
                    aria-label={productName}
                >
                    <FaRegEye className="product-card-icon" />
                </button>
            </div>
        </article>
    );
}

export default ProductCard;
